// ── BUDGET JUSTIFICATION + DOWNLOADS ─────────────────────────
// Sits under a built budget on the Build tab.
//   Justification — AI writes each line's justification to the funder's cost
//     rules (and the funder template's instructions, once one is uploaded).
//     Every line stays editable and can be rewritten on its own; the server
//     flags any figure that doesn't match the line.
//   Download — NofoDesk's standard template, or the funder's own template
//     filled in and handed back, with its formulas and formatting intact.
import { useState, useEffect, useRef } from "react";
import { FONT_UI, FONT_MONO } from "../theme";
import { API, apiFetch } from "../utils/api";

const AMBER = "#d97706", RED = "#dc2626";
const CATEGORIES = ["Personnel", "Fringe Benefits", "Travel", "Equipment", "Supplies", "Contractual", "Construction", "Training", "Other Direct Costs"];
const num = (v) => Number(String(v ?? "").replace(/[^0-9.-]/g, "")) || 0;

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function BudgetDocuments({ T, card, label, input, btn, money, budget, context, onEdit, onJustified }) {
  const cur = budget.currency || "USD";
  const [note, setNote]             = useState("");
  const [busy, setBusy]             = useState(null);
  const [error, setError]           = useState("");
  const [concerns, setConcerns]     = useState({});
  const [narratives, setNarratives] = useState([]);
  const [tpl, setTpl]               = useState(null);
  const [tError, setTError]         = useState("");
  const fileRef = useRef(null);
  const firstRender = useRef(true);
  const lineCount = budget.lines.length;

  // Any change to the lines makes the template preview out of date, and a
  // removed line shifts every flag after it, so flags are cleared.
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setTpl((t) => (t ? { ...t, stale: true } : t));
  }, [budget]);
  useEffect(() => { setConcerns({}); }, [lineCount]);

  const title = { fontFamily: FONT_UI, fontSize: 15, fontWeight: 700, color: T.text, margin: "0 0 4px" };
  const sub   = { fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, margin: "0 0 12px", lineHeight: 1.55 };
  const note_ = (c) => ({ padding: "10px 12px", borderRadius: 8, fontFamily: FONT_UI, fontSize: 12.5, lineHeight: 1.55, background: `${c}14`, border: `1px solid ${c}55`, color: T.textBody, marginTop: 10 });
  const th = { textAlign: "left", padding: "7px 8px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { padding: "7px 8px", fontFamily: FONT_UI, fontSize: 12.5, color: T.textBody, borderBottom: `1px solid ${T.border}`, verticalAlign: "middle" };
  const small = { ...btn(false), padding: "4px 10px", minHeight: 26, fontSize: 12 };
  const calc = (l) => {
    const q = num(l.quantity), c = num(l.unitCost), m = num(l.months) || 1;
    return `${q.toLocaleString()}${l.unit ? ` ${l.unit}` : ""} × ${money(c, cur)}${m !== 1 ? ` × ${m} months` : ""} = ${money(q * c * m, cur)}`;
  };
  const requirements = tpl?.template?.requirements || [];

  const justify = async (only) => {
    setBusy(only == null ? "all" : only); setError("");
    try {
      const res = await apiFetch(`${API}/budget/justify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ budget, grantData: context.grantData, orgProfile: context.orgProfile, mode: context.mode, purpose: context.purpose, months: context.months, requirements, instruction: note, only: only ?? null }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || `Server error ${res.status}`);
      onJustified(Object.fromEntries(json.lines.map((x) => [x.index, x.justification])));
      setConcerns((c) => {
        const n = only == null ? {} : { ...c };
        for (const x of json.lines) { if (x.concern) n[x.index] = x.concern; else delete n[x.index]; }
        return n;
      });
      if (only == null && json.categories?.length) setNarratives(json.categories);
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  const exportStandard = async () => {
    setBusy("export"); setTError("");
    try {
      const res = await apiFetch(`${API}/budget/export`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ budget, meta: { title: context.title, organisation: context.organisation, funder: context.funder, months: context.months, narratives, assumptions: context.assumptions } }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `Server error ${res.status}`); }
      saveBlob(await res.blob(), `${context.title || "Budget"}.xlsx`);
    } catch (e) { setTError(e.message); }
    setBusy(null);
  };

  const analyse = async (file) => {
    if (!file) return;
    setBusy("analyse"); setTError(""); setTpl(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("budget", JSON.stringify(budget));
      const res = await apiFetch(`${API}/budget/template/analyse`, { method: "POST", body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || `Server error ${res.status}`);
      setTpl({ file, template: json.template, plan: json.plan, overrides: {}, stale: false });
    } catch (e) { setTError(e.message); }
    setBusy(null);
  };

  const replan = async (overrides) => {
    setBusy("plan"); setTError("");
    try {
      const res = await apiFetch(`${API}/budget/template/plan`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ budget, template: tpl.template, overrides }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || `Server error ${res.status}`);
      setTpl((t) => ({ ...t, plan: json.plan, overrides, stale: false }));
    } catch (e) { setTError(e.message); }
    setBusy(null);
  };

  const fill = async () => {
    setBusy("fill"); setTError("");
    try {
      const fd = new FormData();
      fd.append("file", tpl.file);
      fd.append("budget", JSON.stringify(budget));
      fd.append("template", JSON.stringify(tpl.template));
      fd.append("overrides", JSON.stringify(tpl.overrides));
      const res = await apiFetch(`${API}/budget/template/fill`, { method: "POST", body: fd });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `Server error ${res.status}`); }
      const ext = /\.xlsm$/i.test(tpl.file.name) ? ".xlsm" : ".xlsx";
      saveBlob(await res.blob(), `${tpl.file.name.replace(/\.xls[xm]$/i, "")} - completed${ext}`);
    } catch (e) { setTError(e.message); }
    setBusy(null);
  };

  const plan = tpl?.plan;
  const hasAny = budget.lines.some((l) => l.justification);

  return (
    <>
      {/* Justification */}
      <div style={card}>
        <div style={title}>Budget justification</div>
        <p style={sub}>
          Written to {context.mode === "grant" ? "the funder's cost rules" : "your budget"}{requirements.length ? " and the instructions in the funder's template" : ""}.
          Each justification quotes the line's calculation: the AI never changes a number, and any figure that doesn't match the line is flagged.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
          <input style={{ ...input, flex: "1 1 280px" }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything to emphasise? e.g. salaries follow the approved national scale" />
          <button style={{ ...btn(true), opacity: busy != null ? 0.6 : 1 }} disabled={busy != null} onClick={() => justify(null)}>
            {busy === "all" ? "Writing…" : hasAny ? "Rewrite all" : "Write justifications"}
          </button>
        </div>
        {error && <div style={note_(RED)}>{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
          {budget.lines.map((l, i) => (
            <div key={i} style={{ padding: "12px 0", borderTop: `1px solid ${T.borderDim || T.border}` }}>
              <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", marginBottom: 6 }}>
                <span style={{ fontFamily: FONT_UI, fontSize: 13, fontWeight: 600, color: T.text }}>{i + 1}. {l.description || "Untitled line"}</span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: T.textMuted }}>{calc(l)}</span>
                <span style={{ flex: 1 }} />
                <button style={{ ...small, opacity: busy != null ? 0.6 : 1 }} disabled={busy != null} onClick={() => justify(i)}>
                  {busy === i ? "Rewriting…" : l.justification ? "Rewrite" : "Write"}
                </button>
              </div>
              <textarea style={{ ...input, minHeight: 54, resize: "vertical", fontSize: 12.5, lineHeight: 1.5 }} value={l.justification || ""}
                placeholder="Why this cost is needed, and how the amount was estimated." onChange={(e) => onEdit(i, "justification", e.target.value)} />
              {concerns[i] && <div style={{ marginTop: 6, fontFamily: FONT_UI, fontSize: 12, color: AMBER, lineHeight: 1.5 }}>⚠ {concerns[i]}</div>}
            </div>
          ))}
        </div>
        {narratives.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <span style={label}>Category narratives</span>
            {narratives.map((n, i) => (
              <div key={n.category} style={{ marginBottom: 10 }}>
                <div style={{ fontFamily: FONT_UI, fontSize: 12.5, fontWeight: 600, color: T.text, marginBottom: 4 }}>{n.category}</div>
                <textarea style={{ ...input, minHeight: 60, resize: "vertical", fontSize: 12.5, lineHeight: 1.5 }} value={n.narrative}
                  onChange={(e) => setNarratives((all) => all.map((x, j) => (j === i ? { ...x, narrative: e.target.value } : x)))} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Downloads */}
      <div style={card}>
        <div style={title}>Download</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18, marginTop: 8 }}>
          <div>
            <span style={label}>NofoDesk template</span>
            <p style={sub}>A clean budget with live Excel formulas, plus a Budget Justification sheet grouped by category.</p>
            <button style={btn(false)} disabled={busy != null} onClick={exportStandard}>{busy === "export" ? "Preparing…" : "Download budget (.xlsx)"}</button>
          </div>
          <div>
            <span style={label}>The funder's own template</span>
            <p style={sub}>Upload the funder's budget template. NofoDesk finds where each line goes, shows you, then fills it in, leaving the funder's formulas and formatting as they are.</p>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => { analyse(e.target.files?.[0]); e.target.value = ""; }} />
            <button style={btn(false)} disabled={busy != null} onClick={() => fileRef.current?.click()}>
              {busy === "analyse" ? "Reading the template…" : tpl ? "Use a different template" : "Upload funder template"}
            </button>
          </div>
        </div>
        {tError && <div style={note_(RED)}>{tError}</div>}

        {tpl && plan && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.text, marginBottom: 10 }}>
              <b>{tpl.file.name}</b>: budget table found on sheet "{tpl.template.sheet}". {plan.placements.length} of {budget.lines.length} lines placed.
            </div>
            {tpl.stale && (
              <div style={{ ...note_(AMBER), marginTop: 0, marginBottom: 10 }}>
                The budget has changed since this check. <button style={small} disabled={busy != null} onClick={() => replan(tpl.overrides)}>{busy === "plan" ? "Updating…" : "Update the preview"}</button> The download always uses your latest lines.
              </div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Template section</th><th style={th}>Takes</th><th style={{ ...th, textAlign: "right" }}>Rows</th><th style={{ ...th, textAlign: "right" }}>Filled</th></tr></thead>
                <tbody>{plan.sections.map((s) => (
                  <tr key={s.index}>
                    <td style={td}>{s.label}</td>
                    <td style={td}>
                      <select style={{ ...input, padding: "5px 8px", fontSize: 12.5, width: "auto" }} value={s.category || ""} disabled={busy != null}
                        onChange={(e) => replan({ ...tpl.overrides, [s.index]: e.target.value || null })}>
                        <option value="">Any cost not placed elsewhere</option>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td style={{ ...td, textAlign: "right", fontFamily: FONT_MONO }}>{s.rows}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: FONT_MONO, color: s.used === s.rows && s.rows > 0 ? AMBER : T.textBody }}>{s.used}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {plan.overflow.length > 0 && (
              <div style={note_(AMBER)}>
                <b>{plan.overflow.length} line{plan.overflow.length === 1 ? "" : "s"} won't fit:</b>
                {plan.overflow.map((o) => <div key={o.index}>• {o.description}: {o.reason}</div>)}
                <div style={{ marginTop: 4 }}>Insert more rows in that section of the template in Excel, save, and upload it again, or send the lines to another section above.</div>
              </div>
            )}
            {plan.notes?.length > 0 && <div style={note_(AMBER)}>{plan.notes.map((n, i) => <div key={i}>• {n}</div>)}</div>}
            {requirements.length > 0 && (
              <div style={{ marginTop: 12, fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, lineHeight: 1.6 }}>
                <b style={{ color: T.text }}>The template asks:</b>
                {requirements.map((r, i) => <div key={i}>• {r}</div>)}
                <div style={{ marginTop: 4 }}>Justifications written now follow these instructions.</div>
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              <button style={{ ...btn(true), opacity: plan.placements.length && busy == null ? 1 : 0.5 }} disabled={!plan.placements.length || busy != null} onClick={fill}>
                {busy === "fill" ? "Filling…" : "Download completed template"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
